class VideoPlayer extends HTMLElement {
  #video = this.querySelector('video');
  #videoOverlay = this.parentElement?.querySelector('[data-video-overlay]');
  #playButton = this.querySelector('[data-action="play-pause-video"]');
  #soundButton = this.querySelector('[data-action="sound-toggle"]');
  #lazy = this.hasAttribute('data-lazy');
  #autoplay = this.hasAttribute('data-autoplay');
  #intersectionObserver = null;
  #userInteracted = false;

  connectedCallback() {
    if (!this.#video) return;

    this.#handleReducedMotion();
    this.#handleDataOrPowerSave();

    if (this.#lazy) {
      this.#intersectionObserver = new IntersectionObserver(this.#handleIntersection);
      this.#intersectionObserver.observe(this.#video);
    }

    this.#video.addEventListener('click', this.#handleClick);
    this.#videoOverlay?.addEventListener('click', this.#handleClick);
    this.#playButton?.addEventListener('click', this.#handleClick);
    this.#soundButton?.addEventListener('click', this.#handleSoundClick);
  }

  disconnectedCallback() {
    this.#intersectionObserver?.disconnect();
    this.#video?.removeEventListener('click', this.#handleClick);
    this.#videoOverlay?.removeEventListener('click', this.#handleClick);
    this.#playButton?.removeEventListener('click', this.#handleClick);
    this.#soundButton?.removeEventListener('click', this.#handleSoundClick);
  }

  #handleIntersection = (entries) => {
    for (const entry of entries) {
      if (!this.#userInteracted && !this.#autoplay) return;
      entry.isIntersecting ? this.#video.play() : this.#video.pause();
      this.#toggleButton();
    }
  };

  #handleReducedMotion() {
    if (!this.#autoplay) return;
    if (!matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#autoplay = false;
    this.#video.pause();
    this.#toggleButton();
  }

  #handleDataOrPowerSave() {
    if (!this.#autoplay || !this.#isMobile()) return;

    if (navigator.connection?.saveData) {
      this.#autoplay = false;
      this.#video.pause();
      this.#toggleButton();
      return;
    }

    if (typeof navigator.getBattery === 'function') {
      navigator.getBattery().then((battery) => {
        if (!this.#autoplay) return;
        if (!battery.charging && battery.level < 0.2) {
          this.#autoplay = false;
          this.#video.pause();
          this.#toggleButton();
        }
      });
    }
  }

  #isMobile() {
    return matchMedia('(max-width: 768px)').matches || matchMedia('(pointer: coarse)').matches;
  }

  #handleClick = () => {
    this.#video.paused ? this.#video.play() : this.#video.pause();
    this.#userInteracted = true;
    this.#toggleButton();
  };

  #handleSoundClick = () => {
    this.#video.muted = !this.#video.muted;
    this.#toggleSoundButton();
  };

  play() {
    if (!this.#video) return;
    this.#userInteracted = true;
    this.#video.play();
    this.#toggleButton();
  }

  pause() {
    if (!this.#video) return;
    this.#video.pause();
    this.#toggleButton();
  }

  #toggleButton() {
    const playIcon = this.#playButton?.querySelector('[data-icon="regular-play"]');
    const pauseIcon = this.#playButton?.querySelector('[data-icon="regular-pause"]');
    playIcon?.classList.toggle('hidden', !this.#video.paused);
    pauseIcon?.classList.toggle('hidden', this.#video.paused);
  }

  #toggleSoundButton() {
    const soundIcon = this.#soundButton?.querySelector('[data-icon="regular-volume-on"]');
    const muteIcon = this.#soundButton?.querySelector('[data-icon="regular-volume-off"]');
    soundIcon?.classList.toggle('hidden', this.#video.muted);
    muteIcon?.classList.toggle('hidden', !this.#video.muted);
  }
}

customElements.define('video-player', VideoPlayer);
